import * as PropTypes from 'prop-types';
import * as React from 'react';
import * as classNames from 'classnames';
import CategorySelect from '../category_select';
import CountrySelect from '../country_select';
import { HashUtil } from '@reverbdotcom/discovery-ui';
import Tooltip from '../tooltip';

const MAX_LENGTHS = {
  make: 40,
  model: 100,
  upc: 14,
  year: 15,
  finish: 100,
};

const LISTING_FIELDS = [
  'year',
  'finish',
  'countryOfOrigin',
];

// If we aren't getting these values from the CSP, we won't require the user to enter them.
const OPTIONAL_FIELDS = [
  'countryOfOrigin',
];

function tooManyCaps(title) {
  if (!title) { return false; }

  const caps = title.match(/[A-Z]/g);
  const capsCount = caps && caps.length;
  const capsRatio = capsCount / title.length;
  const lotsOfCapsInARow = title.match(/[A-Z\s]{10,}/);

  return (title.length > 5 && lotsOfCapsInARow && (capsCount >= 10 || capsRatio >= 0.5));
}

class ListingAttributes extends React.Component {
  constructor(props) {
    super(props);

    const prefilledAttributes = [];
    const emptyAttributes = [];

    LISTING_FIELDS.forEach((attribute) => {
      if (props.values[attribute] === null) {
        if (!OPTIONAL_FIELDS.includes(attribute)) {
          emptyAttributes.push(attribute);
        }
      } else {
        prefilledAttributes.push(attribute);
      }
    });

    this.state = {
      values: this.setValues(),
      showCharacterCounter: false,
      prefilledAttributes,
      emptyAttributes,
    };

    this.onTitleFocus = this.onTitleFocus.bind(this);
  }

  setValues() {
    if (this.props.canonicalProductProps.length > 1) {
      return _.pick(this.props.canonicalProductProps[0], ['country_of_origin', 'finish', 'make', 'model', 'title', 'year', 'upc']);
    }

    return this.props.values;
  }

  onTitleFocus(evt) {
    this.setState({
      showCharacterCounter: true,
      values: { title: evt.target.value },
    });
  }

  renderSellInput({
    inputName, labelName, html = null, editable = false, tooltipText = null, inputMaxlength = null,
  }) {
    const classes = classNames('form-group', { 'd-none': !editable });

    return (
      <div
        className={classes}
        key={inputName}
      >
        <div className="form-group__header">
          <label htmlFor={`product_${inputName}`}>
            {labelName}
            { tooltipText ?
              <Tooltip
                title={tooltipText}
                className="hidden-mobile ml-half"
              /> : null }
          </label>
        </div>
        <div className="form-group__fields">
          {html ||
            <input
              name={`product[${inputName}]`}
              id={`product_${inputName}`}
              data-listing-form={inputName}
              type="text"
              data-pg-match="required"
              hidden={!editable}
              value={this.state.values[inputName]}
              maxLength={inputMaxlength}
              onChange={evt => this.setState({ values: { [inputName]: evt.target.value } })}
            />
          }
        </div>
      </div>
    );
  }

  renderSelectInput({
    inputName, labelName, options
  }) {
    return (
      <div className="form-group" key={inputName}>
        <div className="form-group__header">
          <label htmlFor={`product_${inputName}`}>
            {labelName}
          </label>
        </div>
        <div className="form-group__fields">
          <div className="styled-dropdown">
            <select
              name={`product[${inputName}]`}
              id={`product_${inputName}`}
              data-listing-form={inputName}
              data-pg-match="required"
              required
              onChange={evt => this.setProductStates(evt.target.value, options)}
            >
              <option
                selected
                disabled
                value=""
              >
                {I18n.t('listingForm.attributes.product.labelHint')}
              </option>
              {options.map((option) =>
                <option value={option['id']}>{option['option_title']}</option>
              )}
            </select>
            <input type="hidden" name="product[seed_type]" value="CanonicalProduct" />
          </div>
        </div>
      </div>
    );
  }

  setProductStates(id, options) {
    const cp = this.getCanonicalProduct(id, options);
    this.setState({ values: { ..._.pick(cp, ['country_of_origin', 'finish', 'make', 'model', 'title', 'year', 'upc']) } });
  }

  getCanonicalProduct(id, options) {
    return options.find(o => o['id'] === parseInt(id, 10));
  }

  renderFields() {
    if (this.props.showAllFields) {
      return this.renderAllFields();
    }

    return this.renderHiddenFields();
  }

  renderHiddenFields() {
    return (
      <div>
        {this.renderSellInput({
          inputName: 'make',
          labelName: I18n.t('listingForm.attributes.make.label'),
        })}
        {this.renderSellInput({
          inputName: 'model',
          labelName: I18n.t('listingForm.attributes.model.label'),
        })}
        {this.renderSellInput({
          inputName: 'upc',
          labelName: I18n.t('listingForm.attributes.upc.label'),
        })}
        {this.state.prefilledAttributes.map((attribute) => {
          if (attribute === 'countryOfOrigin') {
            return this.renderCountryOfOrigin({ hidden: true });
          }
          return this.renderSellInput({
            inputName: attribute,
            labelName: I18n.t(`listingForm.attributes.${attribute}.label`),
          });
        })}
        <CategorySelect
          selectedCategoryIds={this.props.categoriesProps.selectedCategoryIds}
          categories={this.props.categoriesProps.categories}
          inputName={this.props.categoriesProps.inputName}
          persisted={this.props.categoriesProps.persisted}
          shippingProfileUrl={this.props.categoriesProps.shippingProfileUrl}
          hidden
        />
        {this.renderCanonicalProductsSelect()}
        {this.renderMissingFields()}
      </div>
    );
  }

  renderCanonicalProductsSelect() {
    if (this.props.canonicalProductProps.length <= 1) { return null; }

    return (
      <div className="scaling-mb-2">
        {
          this.renderSelectInput({
            inputName: 'seed_id',
            labelName: I18n.t('listingForm.attributes.product.label'),
            options: this.props.canonicalProductProps,
          })
        }
      </div>
    );
  }

  renderMissingFields() {
    if (this.state.emptyAttributes.length === 0) { return null; }

    return (
      <div className="scaling-mb-2">
        {this.state.emptyAttributes.map((attribute) => {
          if (attribute === 'countryOfOrigin') {
            return this.renderCountryOfOrigin({ hidden: true });
          }

          return this.renderSellInput({
            inputName: attribute,
            labelName: I18n.t(`listingForm.attributes.${attribute}.label`),
            editable: this.props.canonicalProductProps.length <= 1,
            inputMaxlength: MAX_LENGTHS[attribute],
          });
        })}
      </div>
    );
  }

  renderAllFields() {
    return (
      <div>
        <div className="g-container scaling-mb-2">
          <div className="g-col-4 g-col-mobile-12">
            {this.renderSellInput({
              inputName: 'make',
              labelName: I18n.t('listingForm.attributes.make.label'),
              editable: true,
              inputMaxlength: MAX_LENGTHS.make,
            })}
          </div>
          <div className="g-col-8 g-col-mobile-12">
            {this.renderSellInput({
              inputName: 'model',
              labelName: I18n.t('listingForm.attributes.model.label'),
              editable: true,
              inputMaxlength: MAX_LENGTHS.model,
            })}
            {this.renderSellInput({
              inputName: 'upc',
              labelName: I18n.t('listingForm.attributes.upc.label'),
              inputMaxlength: MAX_LENGTHS.upc,
              hidden: true,
            })}
          </div>
        </div>
        <div className="g-container scaling-mb-2">
          <div className="g-col-4 g-col-mobile-12">
            {this.renderSellInput({
              inputName: 'finish',
              labelName: I18n.t('listingForm.attributes.finish.label'),
              editable: true,
              inputMaxlength: MAX_LENGTHS.finish,
            })}
          </div>
          <div className="g-col-4 g-col-mobile-12">
            {this.renderSellInput({
              inputName: 'year',
              labelName: I18n.t('listingForm.attributes.year.label'),
              editable: true,
              tooltipText: I18n.t('listingForm.attributes.year.tooltip'),
              inputMaxlength: MAX_LENGTHS.year,
            })}
          </div>
          <div className="g-col-4 g-col-mobile-12">
            {this.renderCountryOfOrigin({ hidden: false })}
          </div>
        </div>
        <div className="scaling-mb-2">
          <CategorySelect
            selectedCategoryIds={this.props.categoriesProps.selectedCategoryIds}
            categories={this.props.categoriesProps.categories}
            inputName={this.props.categoriesProps.inputName}
            persisted={this.props.categoriesProps.persisted}
            shippingProfileUrl={this.props.categoriesProps.shippingProfileUrl}
          />
        </div>
      </div>
    );
  }

  renderCountryOfOrigin({ hidden = false }) {
    return (
      this.renderSellInput({
        inputName: 'country_of_origin',
        labelName: I18n.t('listingForm.attributes.country.label'),
        editable: !hidden,
        html: <CountrySelect
          inputName="product[country_of_origin]"
          selectedCountryCode={this.props.values.countryOfOrigin}
          countries={this.props.countries}
          hidden={hidden}
        />,
      })
    );
  }

  renderCharacterCount() {
    if (!this.state.showCharacterCounter) { return null; }

    const titleLength = HashUtil.getSafely(this.state.values, 'title.length', '');
    const charactersLeft = 100 - titleLength;

    return (
      <div>
        {I18n.t(
          'listingForm.localizedContent.charactersLeft',
          { count: charactersLeft },
        )}
      </div>
    );
  }

  renderCapsWarning() {
    if (!tooManyCaps(this.state.values.title)) { return null; }

    return (
      <div className="color-red mt-1">
        {I18n.t('listingForm.warnings.capsWarning')}
      </div>
    );
  }

  renderTitle() {
    return (
      <div className="form-group mb-0">
        <div className="form-group__header fx-justify-between">
          <label htmlFor="product_title">
            {I18n.t('listingForm.attributes.title.label')}
          </label>
          <span className="form-group__help-text mtb-0">
            {this.renderCharacterCount()}
          </span>
        </div>
        <div className="form-group__fields">
          <input
            name="product[title]"
            id="product_title"
            data-listing-form="title"
            type="text"
            data-pg-match="required"
            value={this.state.values.title}
            maxLength="100"
            onChange={evt => this.setState({ values: { title: evt.target.value } })}
            onFocus={this.onTitleFocus}
            onBlur={() => this.setState({ showCharacterCounter: false })}
          />
        </div>
        {this.renderCapsWarning()}
      </div>
    );
  }

  render() {
    return (
      <div className="site-module">
        <div className="site-module__header">
          <h3 className="site-module__title">
            {I18n.t('listingForm.additionalInformation')}
          </h3>
        </div>
        <div className="site-module__content">
          {this.renderFields()}
          {this.renderTitle()}
        </div>
      </div>
    );
  }
}

ListingAttributes.propTypes = {
  showAllFields: PropTypes.bool.isRequired,
  categoriesProps: PropTypes.shape({}).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  canonicalProductProps: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  values: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.string,
    finish: PropTypes.string,
    countryOfOrigin: PropTypes.string,
    title: PropTypes.string,
    upc: PropTypes.string,
  }).isRequired,
};

export default ListingAttributes;



// WEBPACK FOOTER //
// ./javascripts/components/listing_form/listing_attributes.jsx